import { Link } from "react-router-dom";
import { MapPin, Clock, Mic, BadgeCheck, Eye, Radio } from "lucide-react";
import FavoriteButton from "@/components/FavoriteButton";
import { fcfa } from "@/lib/api";

const catLabel = {
  bar: "Bar", maquis: "Maquis", restaurant: "Restaurant", club: "Club",
  lounge: "Lounge", "espace-evenementiel": "Espace",
};

const communeLabel = (s) => (s || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export const isNewEvent = (ev) => ev?.created_at && (Date.now() - new Date(ev.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000;

export const eventStart = (ev) => {
  let t = String(ev?.time || "").trim().replace("h", ":");
  if (/^\d{1,2}:$/.test(t)) t += "00";
  if (/^\d{1,2}$/.test(t)) t += ":00";
  if (!/^\d{1,2}:\d{2}$/.test(t)) t = "00:00";
  const [hh, mm] = t.split(":");
  return new Date(`${ev?.date}T${hh.padStart(2, "0")}:${mm}:00`);
};
export const isPastEvent = (ev) => {
  if (!ev?.date) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${ev.date}T00:00:00`);
  return !isNaN(d.getTime()) && d.getTime() < today.getTime();
};

export function EventCard({ ev }) {
  const past = isPastEvent(ev);
  return (
    <Link to={`/evenement/${ev.slug}`} data-testid={`event-card-${ev.slug}`}
      className="group card-surface rounded-2xl overflow-hidden block transition-all hover:-translate-y-1">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={ev.image || "/media/event-1.jpg"} alt={ev.name} loading="lazy" decoding="async"
          onError={(e) => { e.currentTarget.src = "/media/event-1.jpg"; }}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${past ? "grayscale opacity-70" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        {past ? <span data-testid="event-past-badge" className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-[#F3EBE1] text-[#6E6259] border border-[#EBDDD0] px-2 py-1 rounded-full">Terminé</span> : ev.sponsored ? <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-[#FF5A00] text-white px-2 py-1 rounded-full">Sponsorisé</span> : ev.featured ? <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-[#FFD600] text-black px-2 py-1 rounded-full">★ En avant</span> : isNewEvent(ev) && <span data-testid="event-new-badge" className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-[#FFD600] text-[#1A1412] px-2 py-1 rounded-full animate-pulse">✨ Nouveau</span>}
        <div className="absolute top-3 right-3"><FavoriteButton type="event" itemId={ev.id} className="w-9 h-9 bg-black/50 backdrop-blur" /></div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2 text-[11px] font-semibold text-white/90 mb-1">
            <Clock className="w-3.5 h-3.5 text-[#FF5A00]" /> {ev.time}
            <span className="mx-1">·</span>
            <MapPin className="w-3.5 h-3.5 text-[#FF5A00]" /> {communeLabel(ev.commune)}
          </div>
          <h3 className="font-display font-bold text-lg leading-tight line-clamp-2 text-white">{ev.name}</h3>
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs text-slate-400 mb-2 truncate">{ev.establishment_name}</p>
        <div className="flex items-center justify-between">
          {ev.artist ? <span className="text-xs font-semibold text-slate-200 flex items-center gap-1 truncate"><Mic className="w-3.5 h-3.5 text-[#FF5A00]" /> {ev.artist}</span> : <span />}
          <span className="text-sm font-bold text-flame">{ev.price ? fcfa(ev.price) : "Gratuit"}</span>
        </div>
      </div>
    </Link>
  );
}

export function EstablishmentCard({ est }) {
  return (
    <Link to={`/etablissement/${est.slug}`} data-testid={`establishment-card-${est.slug}`}
      className="group card-surface rounded-2xl overflow-hidden block transition-all hover:-translate-y-1">
      <div className="relative aspect-[16/10] overflow-hidden">
        {est.cover ? (
          <>
            <img src={est.cover} alt={est.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: "radial-gradient(circle at 28% 22%, #38B5D8 0%, #1A5366 42%, #123B4A 100%)" }}>
            <span className="font-display font-black text-white/30 text-6xl select-none">{(est.name || "?").charAt(0).toUpperCase()}</span>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d2c38]/80 to-transparent" />
          </div>
        )}
        {est.is_live ? (
          <span data-testid={`est-live-badge-${est.slug}`} className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#FF1493] text-white px-2 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> En direct</span>
        ) : (
          <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-black/40 text-white backdrop-blur px-2 py-1 rounded-full">{catLabel[est.category] || est.category}</span>
        )}
        {est.plan === "premium" && <span className="absolute top-3 right-3 text-[10px] font-bold bg-[#FF5A00] text-white px-2 py-1 rounded-full">PREMIUM</span>}
        <div className="absolute bottom-3 left-3 right-3">
          {est.sponsored && <span className="inline-block mb-1 text-[10px] font-bold uppercase tracking-wider bg-[#FF5A00] text-white px-2 py-0.5 rounded-full">Sponsorisé</span>}
          <h3 className="font-display font-bold text-lg flex items-center gap-1.5 text-white leading-tight"><span className="truncate">{est.name}</span>{est.verified && <BadgeCheck className="w-4 h-4 shrink-0 text-[#FF5A00]" />}</h3>
          <p className="text-xs text-white/80 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {communeLabel(est.commune)}, Abidjan</p>
        </div>
      </div>
    </Link>
  );
}

export function LiveCard({ live }) {
  return (
    <Link to={`/live/${live.id}`} data-testid={`live-card-${live.id}`}
      className="group card-surface rounded-2xl overflow-hidden block transition-all hover:-translate-y-1">
      <div className="relative aspect-video overflow-hidden">
        <img src={live.image} alt={live.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <span className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] font-bold bg-[#FF5A00] text-white px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE
        </span>
        <span className="absolute top-3 right-3 flex items-center gap-1 text-[11px] font-semibold bg-black/60 text-white backdrop-blur px-2 py-1 rounded-full">
          <Eye className="w-3.5 h-3.5" /> {Number(live.viewers).toLocaleString("fr-FR")}
        </span>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-xs text-white/80">{live.establishment_name}</p>
          <h3 className="font-display font-bold text-base line-clamp-1 text-white">{live.title}</h3>
        </div>
      </div>
    </Link>
  );
}

export function SectionHeader({ emoji, title, action }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4 sm:mb-6">
      <h2 className="font-display section-title text-xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
        {emoji && <span className="mr-2">{emoji}</span>}{title}
      </h2>
      {action && <div className="shrink-0 whitespace-nowrap">{action}</div>}
    </div>
  );
}

export function CardRail({ children, cols = "sm:grid-cols-2 lg:grid-cols-4", testId }) {
  return (
    <div data-testid={testId} className={`flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-px-4 sm:scroll-px-0 no-scrollbar -mx-4 px-4 pb-2 sm:grid sm:gap-6 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0 ${cols}`}>
      {[].concat(children).map((c, i) => <div key={c?.key ?? i} className="w-[82%] shrink-0 snap-start sm:w-auto">{c}</div>)}
    </div>
  );
}
